import { Group } from '@mantine/core';
import { IconHtml, IconMessageCircle, IconSettings } from '@tabler/icons-react';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Form, NewFormsRecipient, UpdateForm, VerifiedEmailsResponse } from '@/api';
import { API_BASE_URL } from '../../constants';
import { generateCurlFormData, generateCurlJson } from '../../lib/form-spec-to-curl';
import { generateHtmlFromSpecV2, generateLLMPrompt } from '../../lib/form-spec-to-html';
import { parseAndValidateFormSpec } from '../../lib/form-specs';
import {
  exampleCurlForm,
  exampleCurlJson,
  exampleCurlMulitpart,
  exampleFormHtml,
  exampleFormHtmlFileUpload,
  exampleLLMPrompt,
} from '../../lib/forms';
import { useRustyState } from '../../state';
import { ChatAssistant } from './ChatAssistant';
import { EditForm, type FormDataRef } from './Edit';
import './form.module.scss';
import { FormEditSectionTabs, type SectionTab } from './FormEditSectionTabs';
import { IntegrationHelp } from './Integration';

// scroll offset clears the sticky tab strip when anchor-jumping
const SECTION_SCROLL_MARGIN_TOP = 80;

const AccountMessagesStartPage = lazy(() =>
  import('../../pages/Account/Messags/Start.page').then((module) => ({
    default: module.AccountMessagesStartPage,
  }))
);

export interface FormDetailProps {
  form: Form;
  submitFormCb: (id: string, newForm: UpdateForm) => Promise<void>;
  deleteCb?: (formId: string) => Promise<void>;

  submitFormRecipientCb: (newForm: NewFormsRecipient) => Promise<void>;
  getVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  getFormVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  deleteRecipientCp: (formId: string, recipientId: string) => Promise<void>;
}

export function FormDetail(props: FormDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [editFormDataRef, setEditFormDataRef] = useState<React.RefObject<FormDataRef> | null>(null);
  const [chatOpened, setChatOpened] = useState(false);
  const handleFormDataRef = useCallback((ref: React.RefObject<FormDataRef>) => {
    setEditFormDataRef(ref);
  }, []);
  const toggleChat = useCallback(() => setChatOpened((prev) => !prev), []);

  const handleDelete = useCallback(async () => {
    if (!props.deleteCb) return;
    await props.deleteCb(props.form.id);
    navigate('/account/forms');
  }, [props.deleteCb, props.form.id, navigate]);

  // Memoize URLs - only recalculate when form ID changes
  const urls = useMemo(
    () => ({
      url: `${API_BASE_URL}/digest/${props.form.id}`,
      challengeUrl: `${API_BASE_URL}/forms/${props.form.id}/challenge/altcha/v3`,
      challengeUrlNoTrack: `${API_BASE_URL}/forms/${props.form.id}/challenge/altcha/v3?track=false`,
    }),
    [props.form.id]
  );

  // Parse TOML once and memoize - only recompute when specs change
  const parsedSpec = useMemo(() => {
    if (!props.form.specs || props.form.specs.trim() === '') {
      return null;
    }
    const result = parseAndValidateFormSpec(props.form.specs);
    return result.isValid && result.formSpec ? result.formSpec : null;
  }, [props.form.specs]);

  // Derive hasFormSpec from parsed spec
  const hasFormSpec = useMemo(
    () => parsedSpec !== null && Object.keys(parsedSpec.fields ?? {}).length > 0,
    [parsedSpec]
  );

  // Derive hasFileField from parsed spec
  const hasFileField = useMemo(() => {
    if (!parsedSpec?.fields) return true; // Default to true if no spec
    return Object.values(parsedSpec.fields).some((field) => field.field === 'file');
  }, [parsedSpec]);

  // Generate examples once - only regenerate when dependencies change
  const examples = useMemo(() => {
    if (!hasFormSpec || !parsedSpec) {
      return {
        curlExample: exampleCurlJson(urls.url),
        curlMultipartExample: exampleCurlMulitpart(urls.url),
        curlFormExample: exampleCurlForm(urls.url),
        formExample: exampleFormHtml(urls.url, props.form.check_challenge, urls.challengeUrl),
        formExampleFileUpload: exampleFormHtmlFileUpload(
          urls.url,
          props.form.check_challenge,
          urls.challengeUrl
        ),
        llmExample: exampleLLMPrompt(urls.url, props.form.check_challenge, urls.challengeUrl),
      };
    }

    return {
      curlExample: generateCurlJson(parsedSpec, urls.url),
      curlMultipartExample: generateCurlFormData(parsedSpec, urls.url),
      curlFormExample: generateCurlFormData(parsedSpec, urls.url),
      formExample: generateHtmlFromSpecV2(
        parsedSpec,
        urls.url,
        props.form.check_challenge,
        urls.challengeUrl
      ),
      formExampleFileUpload: generateHtmlFromSpecV2(
        parsedSpec,
        urls.url,
        props.form.check_challenge,
        urls.challengeUrl
      ),
      llmExample: generateLLMPrompt(
        parsedSpec,
        urls.url,
        props.form.check_challenge,
        urls.challengeUrl
      ),
    };
  }, [hasFormSpec, parsedSpec, urls, props.form.check_challenge, hasFileField]);

  const sectionTabs: SectionTab[] = useMemo(
    () => [
      { id: 'integration', label: t('formEdit.sectionIntegration'), icon: <IconHtml size={16} /> },
      { id: 'settings', label: t('formEdit.sectionSettings'), icon: <IconSettings size={16} /> },
      {
        id: 'messages',
        label: t('formEdit.sectionMessages'),
        icon: <IconMessageCircle size={16} />,
      },
    ],
    [t]
  );

  return (
    <Group align="flex-start" wrap="nowrap" gap="md">
      <div style={{ flex: 1, minWidth: 0, paddingBottom: 80 }}>
        <FormEditSectionTabs sections={sectionTabs} />
        <section id="integration" style={{ scrollMarginTop: SECTION_SCROLL_MARGIN_TOP }}>
          <IntegrationHelp
            form={props.form}
            url={urls.url}
            challengeUrl={urls.challengeUrl}
            challengeUrlNoTrack={urls.challengeUrlNoTrack}
            hasFormSpec={hasFormSpec}
            hasFileField={hasFileField}
            formExample={examples.formExample}
            formExampleFileUpload={examples.formExampleFileUpload}
            curlExample={examples.curlExample}
            curlMultipartExample={examples.curlMultipartExample}
            curlFormExample={examples.curlFormExample}
            llmExample={examples.llmExample}
          />
        </section>
        <section id="settings" style={{ scrollMarginTop: SECTION_SCROLL_MARGIN_TOP }}>
          <EditForm
            form={props.form}
            formSubmitUrl={urls.url}
            formChallengeUrl={urls.challengeUrl}
            formChallengeUrlNoTrack={urls.challengeUrlNoTrack}
            parsedSpec={parsedSpec}
            hasFormSpec={hasFormSpec}
            hasFileField={hasFileField}
            submitFormCb={props.submitFormCb}
            submitFormRecipientCb={props.submitFormRecipientCb}
            getVerifiedEmails={useRustyState.getState().getAndSetVerifiedEmails}
            getFormVerifiedEmails={props.getFormVerifiedEmails}
            deleteRecipientCp={props.deleteRecipientCp}
            onFormDataRef={handleFormDataRef}
            chatOpened={chatOpened}
            onToggleChat={toggleChat}
            onDelete={handleDelete}
          />
        </section>
        <section id="messages" style={{ scrollMarginTop: SECTION_SCROLL_MARGIN_TOP }}>
          <Suspense fallback={<div>Loading messages...</div>}>
            <AccountMessagesStartPage formId={props.form.id} />
          </Suspense>
        </section>
      </div>
      {editFormDataRef && (
        <ChatAssistant
          formId={props.form.id}
          formDataRef={editFormDataRef}
          form={props.form}
          opened={chatOpened}
          onToggle={toggleChat}
        />
      )}
    </Group>
  );
}
